import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseService } from '@app/services';
import { DirectivesModule, PipesModule, SharedModule, PasswordChecklistComponent, passwordPolicy } from '@app/shared';
import { environment } from '@env/environment';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { AccountsService, AuthService, ISignup } from '..';
import { AuthShellComponent } from '../shell';
import { FormGroupInterface, FormGroupValuesInterface } from './signup.interface';

declare const window: any;

@Component({
  imports: [SharedModule, DirectivesModule, PipesModule, AuthShellComponent, PasswordChecklistComponent],
  selector: 'app-signup',
  templateUrl: './signup.component.html',
})
export class SignupComponent extends BaseService implements OnInit {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly accountsService = inject(AccountsService);

  formSignup: FormGroup;
  termos: boolean = false;

  ngOnInit(): void {
    this.createForm();
    this.initGoogleAuth();
    this.initFacebookAuth();
  }

  createForm(): void {
    this.formSignup = new FormGroup<FormGroupInterface>({
      email: new FormControl('', [RxwebValidators.required(), RxwebValidators.email()]),
      password: new FormControl('', [RxwebValidators.required(), passwordPolicy]),
      confirmPassword: new FormControl('', [RxwebValidators.required(), RxwebValidators.compare({ fieldName: 'password' })]),
    });
  }

  signup(): void {
    const formValues: FormGroupValuesInterface = this.formSignup.getRawValue();
    const { email, password } = formValues;
    this._spin.start();
    const params: ISignup = {
      email: email,
      password: password,
    };
    this.authService
      .signup(params)
      .then(async (): Promise<void> => {
        await this.accountsService.loadAccountIds();
        this._toast.success({ message: 'Conta criada com sucesso!' });
        void this.router.navigateByUrl('/dashboard');
      })
      .catch((e: Error) => {
        this._toast.error({ message: e.message });
      })
      .finally(() => {
        this._spin.stop();
      });
  }

  initGoogleAuth(): void {
    if (!environment.social.googleClientId) return;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.google.accounts.id.initialize({
        client_id: environment.social.googleClientId,
        callback: this.handleGoogleCredentialResponse.bind(this),
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      window.google.accounts.id.renderButton(document.getElementById('google-btn-container-signup'), {
        theme: 'outline',
        size: 'large',
        type: 'standard',
        text: 'signup_with',
        width: 240,
        shape: 'rectangular',
      });
    };
    document.head.appendChild(script);
  }

  handleGoogleCredentialResponse(response: { credential?: string }): void {
    if (response.credential) {
      this._spin.start();
      this.authService
        .socialLoginGoogle(response.credential)
        .then(async (): Promise<void> => {
          await this.accountsService.loadAccountIds();
          this._toast.success({ message: 'Conta criada e autenticada com sucesso!' });
          void this.router.navigateByUrl('/dashboard');
        })
        .catch((e: Error) => {
          this._toast.error({ message: e.message });
        })
        .finally(() => {
          this._spin.stop();
        });
    }
  }

  initFacebookAuth(): void {
    if (!environment.social.facebookAppId) return;
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: environment.social.facebookAppId,
        cookie: true,
        xfbml: true,
        version: 'v18.0',
      });
    };
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/pt_BR/sdk.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  loginWithFacebook(): void {
    if (!window.FB) {
      this._toast.error({ message: 'Facebook SDK não carregado' });
      return;
    }
    window.FB.login(
      (response: { authResponse?: { accessToken?: string } }) => {
        if (response.authResponse && response.authResponse.accessToken) {
          this._spin.start();
          this.authService
            .socialLoginFacebook(response.authResponse.accessToken)
            .then(async (): Promise<void> => {
              await this.accountsService.loadAccountIds();
              this._toast.success({ message: 'Conta criada e autenticada com sucesso!' });
              void this.router.navigateByUrl('/dashboard');
            })
            .catch((e: Error) => {
              this._toast.error({ message: e.message });
            })
            .finally(() => {
              this._spin.stop();
            });
        } else {
          this._toast.error({ message: 'Falha ao autenticar com o Facebook' });
        }
      },
      { scope: 'public_profile,email' }
    );
  }

  checkTermos(): void {
    this.termos = !this.termos;
  }
}
